import { ViewType, ViewObject } from '../base';

import { AppConstants } from '@unpispas/upp-defs';
import { PriceType, PriceInfo } from '@unpispas/upp-defs';
import { dataService } from '../../data';
import { ProductOpt } from '../objects/productopt';

import { CategoryView } from './category';
import { ProductView } from './product';
import { PlaceView } from './place';
import { QrCodeView } from './qrcode';

/****************************/
/* CLASS VIEW               */
/****************************/

export class _ProductOptView extends ViewObject<ProductOpt> {

    /**********************************/
    /* VIEWOBJECT IMPLEMENTATION      */
    /**********************************/

    private get productoption(): ProductOpt{
        return this.object;
    }
             
    constructor(object: ProductOpt, data: dataService){
        super(object, data);
    }

    override OnDestroy(): void {
        super.OnDestroy();
    }

    /**********************************/
    /* VIEW MANAGED INFORMATION        */
    /**********************************/

    private _calcpriceinfo: PriceType | null = null;

    private RefreshView(targets: string[]){
        if (targets.includes('PRODUCTOPT')){
            this._calcpriceinfo = null;
            this.DoRefreshView();
        }

        if (targets.includes('OPTIONCHECKED')){
            this.DoRefreshView();
        }
    }

    /**********************************/
    /* CHILDREN REGISTRATION          */
    /**********************************/

    override doRegister(): void{
        const weakref = new WeakRef(this);

        // deregister of current subscriptions
        for (const _subsription of this._subscriptions){
            _subsription.unsubscribe();
        }
        this._subscriptions = [];

        // register to updates on contained items             
        this._subscriptions.push(
            this.productoption.OnRefresh.subscribe(
            (targets: string[]) => {
                const self = weakref.deref();
                if (self){  // do not keep strong references in subscription
                    self.RefreshView(targets);
                }                
            })
        );
    }

    /**********************************/
    /* ACTIVE CHILDREN INFORMATION    */
    /**********************************/
    
    get product(): ProductView {
        return this.alives.get(this.productoption.product) as ProductView;
    }

    get category(): CategoryView | null {
        return this.alives.get(this.productoption.category) as CategoryView | null;
    }

    /**********************************/
    /* CUSTOM METHODS                 */
    /**********************************/

    private _checked = false;
    get Checked(): boolean {
        return this._checked;
    }

    set Checked(value: boolean){
        this._checked = value;
        this.productoption.DoRefresh('OPTIONCHECKED');
    }

    private _enabled = false;
    get Enabled(): boolean {
        return this._enabled;
    }

    set Enabled(value: boolean){
        this._enabled = value;
        this.productoption.DoRefresh('OPTIONCHECKED');
    }

    get IsAvailable(): boolean {
        return this.productoption.status === 'AC';
    }

    // if category is fixed and the selection limit has been reached
    get Disabled(){
        if (this.Checked == false){
            const _category = this.category;
            if (_category && _category.type == 'FIXED'){
                return _category.Selected.length >= (_category.fxmax || 0)
            }    
        }

        return false;   // enabled
    }

    get priceinfo(): PriceType {
        if (this._calcpriceinfo == null){
            const _p: number = this.productoption.price || 0;
            const _r: number = this.productoption.product?.taxrate ?? AppConstants.defaultTaxRate;
    
            this._calcpriceinfo = new PriceInfo(_p, _r).price;
        }

        return this._calcpriceinfo;
    }

    /**
     * Resolves the price to charge for this option at the ticket's place/table, at instant `at`.
     * Same override lookup priority as `ProductView.tableprice`, applied to `OPTIONPRICE`: active
     * period override in the effective zone → custom zone's default override (`pricetime == null`)
     * → `productoption.price`. A period override never cascades into a different zone than the one
     * it belongs to.
     */
    tableprice(place: PlaceView | null, qrcode: QrCodeView | null, at: Date = new Date()): number {
        const _base = this.productoption.price || 0;

        if (!place){
            return _base;
        }

        const _zoneview = qrcode?.pricezone ?? null;
        const _zone = _zoneview?.object ?? null; // null == default zone

        const _period = place.ActiveTime(at);
        const _rows = this.productoption.prices ?? [];

        if (_period){
            const _periodrow = _rows.find((_row) => _row.IsValid && (_row.pricezone === _zone) && (_row.pricetime === _period));
            if (_periodrow){
                const _exact = _periodrow.price ?? null;
                if (_exact !== null){
                    return _exact;
                }

                // empty period row: fall through to zone default / base (same as GetPriceAt).
            }
        }

        // custom zone default override only (default zone never has pricetime == null)
        if (_zone){
            const _defaultrow = _rows.find((_row) => _row.IsValid && (_row.pricezone === _zone) && (_row.pricetime === null));
            if (_defaultrow){
                const _zoneDefault = _defaultrow.price ?? null;
                if (_zoneDefault !== null){
                    return _zoneDefault;
                }
            }
        }

        return _base;
    }
}

/****************************/
/* PROXY VIEW               */
/****************************/

export type ProductOptView = ViewType <_ProductOptView, ProductOpt>;

export function ProductOptViewProxy(object: ProductOpt, data: dataService): ProductOptView {
    return new _ProductOptView(object, data).Proxy as ProductOptView;
}
